import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import parsePhoneNumber from 'libphonenumber-js';
import countries from 'utils/countries.json';

import {
  POST_LOGIN,
  POST_LOGOUT,
  GET_ME,
  PATCH_USER,
  POST_VERIFY_EMAIL,
  POST_RESEND_VERIFICATION,
  POST_FORGOT_PASSWORD,
  POST_RESET_PASSWORD,
} from 'services/api';
import {
  showSuccessMessage,
  showErrorMessage,
} from 'redux/slices/notification';
import { updateKycStatus } from 'redux/slices/company';
import { rejectValue } from 'redux/helpers';
import {
  AUTH,
  LOGIN_ACTION,
  LOGOUT_ACTION,
  ME_ACTION,
  UPDATE_USER,
  VERIFY_EMAIL,
  RESEND_VERIFICATION,
  FORGOT_PASSWORD_ACTION,
  RESET_PASSWORD_ACTION,
} from './types';
import { DEFAULT_COUNTRY } from 'utils/constants';
import { logEvent } from 'utils/ga';

export const initialState = {
  authenticated: null,
  error: null,
  resetPasswordEmail: '',
  isLoading: null,
  user: {
    initials: '',
    firstName: '',
    lastName: '',
    name: '',
    phone_number: '',
    email: '',
    phone_country: DEFAULT_COUNTRY,
    phone: '',
    isDevOrSuperAdmin: null,
  },
  needVerification: null,
};

export const login = createAsyncThunk(
  LOGIN_ACTION,
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await POST_LOGIN(payload);

      const action = await dispatch(getMe());
      if (action.payload?.status === 423) {
        dispatch(resendVerification());
        dispatch(setNeedVerification(true));
      }
      return data;
    } catch (error) {
      return rejectWithValue(
        rejectValue({ ...error.response, skipNotification: true })
      );
    }
  }
);

export const forgotPassword = createAsyncThunk(
  FORGOT_PASSWORD_ACTION,
  async (email, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await POST_FORGOT_PASSWORD({
        email,
        return_url: `${window.location.origin}/reset-password`,
      });
      return data;
    } catch (error) {
      dispatch(showErrorMessage(error.response?.data?.message));
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const resetPassword = createAsyncThunk(
  RESET_PASSWORD_ACTION,
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await POST_RESET_PASSWORD(payload);
      dispatch(showSuccessMessage({ message: data.message, timeout: 1500 }));

      const action = await dispatch(getMe());
      if (action.payload?.status === 423) {
        dispatch(resendVerification());
        dispatch(setNeedVerification(true));
      }
      return data;
    } catch (error) {
      dispatch(showErrorMessage(error.response?.data?.message));
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const logout = createAsyncThunk(
  LOGOUT_ACTION,
  async (_, { rejectWithValue, getState, dispatch }) => {
    logEvent('sign_out');
    try {
      await POST_LOGOUT();
      if (
        getState().company.entities[getState().company.current]
          ?.account_holder_kyc_just_approved
      ) {
        dispatch(updateKycStatus());
      }
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);
export const getMe = createAsyncThunk(
  ME_ACTION,
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await GET_ME();

      return data;
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);
export const updateUser = createAsyncThunk(
  UPDATE_USER,
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await PATCH_USER(payload);
      dispatch(
        showSuccessMessage({ message: 'Profile updated', timeout: 3000 })
      );
      return data;
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const verifyEmail = createAsyncThunk(
  VERIFY_EMAIL,
  async (code, { dispatch, rejectWithValue }) => {
    try {
      const response = await POST_VERIFY_EMAIL(code);
      return response;
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);
export const resendVerification = createAsyncThunk(
  RESEND_VERIFICATION,
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const response = await POST_RESEND_VERIFICATION();

      return response;
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);
const userFulfilled = (state, { payload }) => {
  const { first_name, last_name, permissions, ..._payload } = payload;
  const firstName = first_name || '';
  const lastName = last_name || '';
  const initials = `${firstName[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  let data = {
    ..._payload,
    name: `${firstName} ${lastName}`,
    firstName,
    lastName,
    initials,
    phone_country: DEFAULT_COUNTRY,
    phone: '',
    isDevOrSuperAdmin: payload.is_super_admin || payload.is_developer,
  };

  if (!!payload.phone_number) {
    const parsedPhone = parsePhoneNumber(payload.phone_number);
    if (parsedPhone) {
      data = {
        ...data,
        phone_country: countries.find(
          (item) => item.dial_code === `+${parsedPhone.countryCallingCode}`
        ),
        phone: parsedPhone.nationalNumber,
      };
    }
  }
  state.user = data;
  state.error = null;
};

const authSlice = createSlice({
  name: AUTH,
  initialState: window.Cypress
    ? { ...initialState, ...window.initialAuthState }
    : initialState,
  reducers: {
    setResetPasswordEmail: (state, { payload }) => {
      state.resetPasswordEmail = payload;
    },
    setNeedVerification: (state, { payload }) => {
      state.needVerification = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state, action) => {
        state.error = null;
        state.user.email = action.meta?.arg?.email || '';
      })
      .addCase(login.fulfilled, (state, action) => {
        userFulfilled(state, action);
      })
      .addCase(login.rejected, (state, action) => {
        state.error = true;
      })
      .addCase(logout.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(logout.rejected, (state, action) => {
        state.isLoading = null;
      })
      .addCase(getMe.pending, (state, action) => {
        state.error = null;
      })
      .addCase(getMe.fulfilled, (state, action) => {
        state.authenticated = true;
        userFulfilled(state, action);
      })
      .addCase(getMe.rejected, (state, { payload }) => {
        if (
          payload?.status === 423 &&
          payload?.message?.indexOf('is not verified') > -1
        ) {
          state.authenticated = false;
        }
      })
      .addCase(updateUser.fulfilled, userFulfilled)
      .addCase(verifyEmail.fulfilled, (state) => {
        state.authenticated = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.resetPasswordEmail = '';
        state.error = null;
      });
  },
});

export const { setResetPasswordEmail, setNeedVerification } = authSlice.actions;

export default authSlice.reducer;
